/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import {createApp, nextTick} from "vue";
import LoadScript, {loadScript} from "vue-plugin-load-script";
import { createI18n } from 'vue-i18n';
import {createRouter, createWebHistory} from "vue-router";
import VueAxios from 'vue-axios';
import { vOnClickOutside } from '@vueuse/components';
import axios from 'axios';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPhone, faCaretDown, faCaretUp, faUser, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
// import { faUserSecret } from '@fortawesome/free-brands-svg-icons';
import { faClock, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
// import { faTwitter } from '@fortawesome/free-brands-svg-icons';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import VueSocialauth from '../js/plugins/oAuth';

import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'


/* add icons to the library */
library.add(faClock, faPhone, faCaretDown, faCaretUp, faTimesCircle, faUser, faArrowLeft, faArrowRight);

import CountryFlag from 'vue3-country-flag-icon';
//@ts-ignore
require('./bootstrap');


//@ts-ignore
import App from './App.vue';
//@ts-ignore
import store from './store';
//@ts-ignore
import {routes} from './routes';

//@ts-ignore
import HeaderTopComponent from "./components/header/headerTop/HeaderTopComponent";
//@ts-ignore
import HeaderMenuComponent from "./components/header/headerMeniu/HeaderMenuComponent";
//@ts-ignore
import FooterComponent from "./components/footer/FooterComponent";

import {stringFilters} from "./filters/stringFilters";
import TranslationService from "./services/translationService";
import {seatCountTranslation} from "./helpers/seatCountTranslation";
// @ts-ignore
import {modalEvents} from "./helpers/modalEvents";
import {disableDates} from "./helpers/disableDates";
import SettingsService from "./services/settingsService";
import NameOfHelper from "./helpers/nameOfHelper";
import * as process from "process";

const router = createRouter({
    history: createWebHistory(), routes,
});

// VueI18n instance
const i18n = createI18n({
    locale: 'en'
});
Bugsnag.start({
    apiKey: `${process.env.MIX_BUGSNAG_API_KEY}`,
    plugins: [new BugsnagPluginVue()],
});

const sessionLifetime = 6 * 60 * 60 * 1000; // 12 hours (in milliseconds)
activityMonitoring();

await TranslationService.loadTranslations(i18n, (default_locale_from_api) => {
    // Get user selected locale to the store if exists
    const localStorageLocale = localStorage.getItem("locale");

    const locale = localStorageLocale ? localStorageLocale : default_locale_from_api;

    TranslationService.setI18nLanguage(i18n, locale);

    store.commit('general/SET_LOCALE', locale);

    //@ts-ignore
    document.querySelector('html').setAttribute('lang', locale);
});

const settings = await SettingsService.load();
const nameOf = (c) => NameOfHelper.obj(c);

/**
 * Add language header for all request
 */
//@ts-ignore
window.axios.interceptors.request.use((config) => {
    // const baseUrl = window.axios.defaults.baseURL;

    // @ts-ignore
    const token = store.state.customer.token;

    if (token) {
        config.headers.common['Authorization'] = ('Bearer ' + token);
    }

    // @ts-ignore
    const locale = store.state.general.locale;

    if (locale) {
        config.headers.common['Accept-Language'] = locale;
        config.headers.common['Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    return config;
});

// Initialize states (before .use(router) for router to interact with store correctly)
store.commit('order/INITIALIZE_ORDER_STATE');
store.commit('orderFalseDriver/INITIALIZE_FALSE_DRIVER_ORDER_STATE');
store.commit('orderTrueDriver/INITIALIZE_TRUE_DRIVER_ORDER_STATE');
store.commit('orderTrueDriverDates/INITIALIZE_TRUE_DRIVER_DATE_STATE');
store.commit('orderFalseRoundTrip/INITIALIZE_FALSE_ROUND_TRIP_STATE');
store.commit('orderTrueRoundTrip/INITIALIZE_TRUE_ROUND_TRIP_STATE');
store.commit('vehicle/INITIALIZE');
store.commit('customer/INITIALIZE');
store.commit('socialAuth/INITIALIZE');

const bugsnagVue = Bugsnag.getPlugin('vue');

const app = createApp(App)
    .use(bugsnagVue!)
    .use(LoadScript)
    .use(i18n)
    .use(VueAxios, axios)
    .use(store)
    .use(router)
    .component('font-awesome-icon', FontAwesomeIcon)
    .component('country-flag', CountryFlag)
    .directive('on-click-outside', vOnClickOutside)
    .component('header-top', HeaderTopComponent)
    .component('header-menu', HeaderMenuComponent)
    .component('footer-container', FooterComponent)
    .component('Datepicker', Datepicker);


app.config.globalProperties.$filters = {
    ...stringFilters,
};

app.config.globalProperties.$helpers = {
    ...seatCountTranslation,
    ...modalEvents,
    ...disableDates,
};

// Social auth settings

// @ts-ignore
const socialAuthState = store.state.socialAuth;

store.commit( 'socialAuth/SET_STATE_PROP_VALUE', [
    {prop: nameOf(() => socialAuthState.google_id), value: settings['GOOGLE_ID'].key_value},
    {prop: nameOf(() => socialAuthState.google_secret), value: settings['GOOGLE_SECRET'].key_value},
    {prop: nameOf(() => socialAuthState.google_uri), value: settings['GOOGLE_URI'].key_value},
    {prop: nameOf(() => socialAuthState.facebook_id), value: settings['FACEBOOK_ID'].key_value},
    {prop: nameOf(() => socialAuthState.facebook_secret), value: settings['FACEBOOK_SECRET'].key_value},
    {prop: nameOf(() => socialAuthState.facebook_uri), value: settings['FACEBOOK_URI'].key_value},
    {prop: nameOf(() => socialAuthState.google_login), value: settings['google_login'].key_value},
    {prop: nameOf(() => socialAuthState.facebook_login), value: settings['facebook_login'].key_value},
    {prop: nameOf(() => socialAuthState.enable_login_setting), value: settings['enable_login_setting'].key_value},
    {prop: nameOf(() => socialAuthState.enable_registration_setting), value: settings['enable_registration_setting'].key_value},
]);

app.use(VueSocialauth, {
    providers: {
        google: {
            clientId: socialAuthState.google_id,
            redirectUri: socialAuthState.google_uri,
        },
        facebook: {
            clientId: socialAuthState.facebook_id,
            redirectUri: socialAuthState.facebook_uri,
        }
    }
});

// 3rd party scripts
await loadScript("https://maps.googleapis.com/maps/api/js?key=" + settings['GOOGLE_API_KEY'].key_value + "&libraries=places,geometry&callback=initMap");
store.commit( 'general/SET_GOOGLE_API_KEY', settings['GOOGLE_API_KEY'].key_value);

app.mount('#app');

// PRIVATE METHODS

/**
 * Activity tracking and store clear after X period of time
 */
function activityMonitoring(): void {
    handleStateClear();

    let timeoutId = <number|null>null;

    ['mousemove', 'touchmove'].forEach(function(e) {
        window.addEventListener(e, () => {
            activityStarted();

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                activityStopped();
            }, 30 * 1000); // Treat activity stopped after 30 sec of idle user on page
        });
    });

    let intervalId = <number|null>null;

    function activityStarted() {
        if (intervalId) {
            return;
        }
        intervalId = setInterval(() => {
            localStorage.setItem('lastActiveDateTime', new Date().toString());
        }, 15 * 1000); // 15sec
    }
    function activityStopped() {
        clearInterval(intervalId as number);

        setTimeout(() => {
            handleStateClear();
        }, 30000);
    }

    /**
     * Clean store if conditions met
     */
    function handleStateClear() {
        const lastActiveDateTime = localStorage.getItem('lastActiveDateTime');
        // For the first time to ensure everything is nice, clear and fresh
        if (lastActiveDateTime === null) {
            localStorage.setItem('lastActiveDateTime', new Date().toString());
            clearState();
            return;
        }

        if (lastActiveDateTime && new Date(lastActiveDateTime).getTime() < Date.now() - sessionLifetime) { // < Current time - 12 hours (in milliseconds)
            clearState();

            localStorage.removeItem('lastActiveDateTime');

            // Ignore redirect for services iframe, because we need to have storage cleared but redirect to home page is not preferred for it.
            const path = window.location.pathname;

            if (path === '/services-iframe') {
                window.location.reload();
                return;
            }

            window.location.href = '/';
        }
    }

    function clearState() {
        // No need to clear store.commit('company/CLEAR_STATE');
        store.commit('customer/CLEAR_STATE');
        // No need to clear store.commit('general/CLEAR_STATE');

        store.commit('order/CLEAR_STATE');

        store.commit('orderFalseDriver/CLEAR_STATE');
        store.commit('orderFalseRoundTrip/CLEAR_STATE');

        store.commit('orderTrueDriver/CLEAR_STATE');
        store.commit('orderTrueDriverDates/CLEAR_STATE');

        store.commit('orderTrueRoundTrip/CLEAR_STATE');
        store.commit('socialAuth/CLEAR_STATE');
        store.commit('vehicle/CLEAR_STATE');
    }
}

